<script setup>
import NModal from '/app/components/NModal.vue';
import GameDash from '/app/components/game-dash/GameDash.vue';
import { useGameDash } from '/app/composables/gamedash';
import { ref, computed, watch, watchEffect, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useGameDashUpdates } from '/app/composables/gamedash-updates';
import { track } from '/app/utils/analytics';
import NIcon from '/app/components/NIcon.vue';
import { useBus } from '/app/composables/bus';
import { hasNudged, markNudged, takePendingNudge, clearPendingNudge, SCOPE } from '/app/composables/gamedash-nudge';
import { prefersReducedMotion } from '/app/utils/motion';
import { ACTIVITY_RECEIVED, DASH_LANDED, OPEN_REQUESTED, POINTS_EARNED, BELL_FLY_TARGET } from '/app/constant/game-dash';

const modal = ref(null);
const route = useRoute();
const openedAt = ref(0);
const isOpen = ref(false);
// What put the dash on screen. Reset on close so a nudged open is never
// reported twice, and a bell click after one is not reported as a nudge.
const trigger = ref('manual');
const { brain, role } = useGameDash();
const { hasUpdates, score, markUnread, markSeen } = useGameDashUpdates();

const emit = defineEmits(['close', 'show']);

// Offset from the middle of the screen, where the modal box sits, to the centre
// of the navbar bell. Handed to the `gamedash-zoom-*` classes as the
// `transform-origin` the panel scales about, so collapsing it to nothing carries
// it to the bell. Null when the bell is off screen or the user asked for less
// motion - the modal then keeps NModal's default centre fade.
const flyOffset = ref(null);

// How long the panel spends travelling, before the fade. Must match the
// transform leg of `.gamedash-zoom-leave-active`.
const TRAVEL_MS = 520;

let landingTimer = null;

// Smallest and largest a bell can reasonably be. The desktop bell is a square
// icon button; the mobile one is a whole labelled row, and a chip as wide as
// "Game Dash" no longer reads as the button.
const BELL_MIN_PX = 28;
const BELL_MAX_PX = 56;

// Layout size of the panel, measured once it is on screen and kept for the
// trips after. Read from `offsetWidth`, not the bounding rect, which is
// mid-transform whenever this matters.
const panelSize = ref(null);

const panelEl = () => document.querySelector('#modals [data-heap-id="gamedash-overlay"]');

const measurePanel = () => {
    const el = panelEl();

    if (el?.offsetWidth) {
        panelSize.value = { width: el.offsetWidth, height: el.offsetHeight };
    }
};

/**
 * The bell the dash should collapse into.
 *
 * Both activators carry the same `data-fly-target`, one per breakpoint, so pick
 * whichever is on screen - and take its parent, the button, rather than the
 * landing pad itself, which is a 12px badge in the corner.
 */
const bellRect = () => {
    const pads = Array.from(document.querySelectorAll(`[data-fly-target="${BELL_FLY_TARGET}"]`));

    return pads
        .map((pad) => (pad.parentElement || pad).getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .find((rect) => {
            const x = rect.left + rect.width / 2;
            const y = rect.top + rect.height / 2;

            return x > 0 && x < window.innerWidth && y > 0 && y < window.innerHeight;
        }) || null;
};

const measureBell = () => {
    const rect = prefersReducedMotion() ? null : bellRect();

    if (!rect) {
        flyOffset.value = null;

        return;
    }

    // First open of the page, before the panel has ever been on screen:
    // `sm:max-w-xl` is 36rem and the dash is built out of vh units. Close
    // enough for one trip, exact for every one after.
    const width = panelSize.value?.width || Math.min(window.innerWidth, 576);
    const height = panelSize.value?.height || Math.round(window.innerHeight * 0.78);

    const diameter = Math.min(Math.max(Math.min(rect.width, rect.height), BELL_MIN_PX), BELL_MAX_PX);

    flyOffset.value = {
        // Origin is relative to the panel's own centre, which is also the
        // centre of the screen.
        '--gd-origin-x': `${Math.round(rect.left + rect.width / 2 - window.innerWidth / 2)}px`,
        '--gd-origin-y': `${Math.round(rect.top + rect.height / 2 - window.innerHeight / 2)}px`,
        // Per axis, so the chip the panel ends as is bell-sized both ways
        // rather than merely small.
        '--gd-end-x': (diameter / width).toFixed(4),
        '--gd-end-y': (diameter / height).toFixed(4),
    };
};

// Vue does not patch attributes on an element it is unmounting, so the `:style`
// binding is the open-time measurement and nothing else. Writing the close-time
// one straight onto the node is what actually reaches the leave transition.
const applyFlyVars = () => {
    const el = panelEl();

    if (!el || !flyOffset.value) {
        return;
    }

    Object.entries(flyOffset.value).forEach(([name, value]) => {
        el.style.setProperty(name, value);
    });
};

const flyTransition = computed(() => (flyOffset.value
    ? {
        enterActiveClass: 'gamedash-zoom-enter-active',
        leaveActiveClass: 'gamedash-zoom-leave-active',
        enterFromClass: 'gamedash-zoom-collapsed',
        enterToClass: 'gamedash-zoom-expanded',
        leaveFromClass: 'gamedash-zoom-expanded',
        leaveToClass: 'gamedash-zoom-collapsed',
    }
    : {}));

const handleClose = () => {
    modal.value?.close();
}

// NModal announces `close` before the leave transition starts, so measuring
// here still feeds it - and it covers every way out, Escape and backdrop
// included. Re-measured rather than reused from open, because the bell moves
// with the breakpoint and with however far the page scrolled while the dash
// was up.
const handleClosed = () => {
    // NModal announces `close` for a modal that was never open - on mount, and
    // on any close of an already-closed one. Nothing to report there.
    if (!isOpen.value) {
        return;
    }

    measureBell();
    applyFlyVars();

    // Ring the bell as the panel lands on it, not as it leaves. Skipped when
    // there is no bell to land on - the modal is doing a plain centre fade.
    clearTimeout(landingTimer);

    if (flyOffset.value) {
        landingTimer = setTimeout(() => bus(DASH_LANDED), TRAVEL_MS);
    }

    track('Game Dash Closed', {
        role: role.value,
        trigger: trigger.value,
        dwell_ms: openedAt.value ? Date.now() - openedAt.value : null,
    });

    isOpen.value = false;
    trigger.value = 'manual';

    emit('close');
}

const handleShow = () => {
    measureBell();

    // Once it is on screen the panel can be measured for real, which is what
    // the next trip - this one's way out - ends its scale against.
    nextTick(() => requestAnimationFrame(measurePanel));

    emit('show');

    isOpen.value = true;
    openedAt.value = Date.now();
    track('Game Dash Opened', {
        role: role.value,
        trigger: trigger.value,
        has_updates: hasUpdates.value,
    });

    markSeen();
}

watch(route, () => {
    handleClose();
});

watchEffect(() => {
    if (role.value !== 'overall') {
        return false;
    }

    if (!brain.value?.score) {
        return;
    }

    // Raises only. This effect flushes after the push handlers below, so
    // assigning the comparison outright would clear a badge those handlers
    // had just lit. Clearing belongs to handleShow, on open.
    if (score.value && score.value !== brain.value.score) {
        hasUpdates.value = true;
    }
});

const { on, emit: bus } = useBus();

onBeforeUnmount(() => {
    clearTimeout(landingTimer);
});

// Optimistic badge: lights the moment a points dot lands on the bell, ahead of
// the brain__score-updated push.
on(POINTS_EARNED, markUnread);

// Real-time pushes that carry no score change - a highlight, a neuron - leave
// the score diff below unmoved, so they light the badge on their own.
on(ACTIVITY_RECEIVED, markUnread);

// Pages ask for the dash by emitting; this is where the request is judged.
// Living here rather than in the caller means a nudge is only ever spent where
// the dash could actually have opened - the dialog exists for signed-in users
// only - so a signed-out visit to /my-dashboard does not silently burn the one
// chance to show it.
const handleOpenRequest = ({ nudge, scope = SCOPE.ONCE } = {}) => {
    clearPendingNudge();

    if (isOpen.value || (nudge && hasNudged(nudge, scope))) {
        return;
    }

    if (nudge) {
        markNudged(nudge, scope);
    }

    trigger.value = nudge || 'programmatic';

    modal.value?.open();
};

on(OPEN_REQUESTED, handleOpenRequest);

// A request emitted while this dialog did not exist yet. On a cold load the
// navbar renders the bell only once `me` has come back, which can land after
// the page that asked - /my-dashboard reaches its own nudge that way.
onMounted(() => {
    const request = takePendingNudge();

    if (request) {
        handleOpenRequest(request);
    }
});

defineExpose({
    open: () => {
        modal.value?.open();
    },
    close: handleClose,
});
</script>

<template>
    <NModal
        ref="modal"
        data-heap-id="gamedash-overlay"
        class="w-full sm:max-w-xl"
        :style="flyOffset"
        :content-transition="flyTransition"
        @show="handleShow"
        @close="handleClosed"
    >
        <template #activator="{ on, close: handleClose }">
            <slot name="activator" :on="on" :hasUpdates="hasUpdates" />
        </template>

        <GameDash class="shadow-2xl mx-4 rounded-none">
            <template #header>
                <h2 class="game-dash-header m-0">
                    <span class="game-dash-header__title leading-none">
                        <NIcon as="bell" class="text-lg text-white mr-2" />
                        {{ $t('component.game-dash.title') }}
                    </span>

                    <button type="button" aria-label="button" class="game-dash-header__btn" @click="handleClose">
                        <NIcon as="close-circle" />
                    </button>
                </h2>
            </template>
        </GameDash>
    </NModal>
</template>